/** Single source of truth for the backend origin (runtime-configurable).
 *
 *  Resolution order (first non-empty wins):
 *   1. runtime config written at container start into /env-config.js
 *      (entrypoint.sh) — any of the common globals:
 *      window.__ENV__ · window._env_ · window.env · window.__RUNTIME_CONFIG__
 *      keys: BACKEND_URL | REACT_APP_BACKEND_URL | API_URL
 *      An unset value reaches the browser as "" and is treated as absent.
 *   2. window.REACT_APP_BACKEND_URL — legacy global
 *   3. build-time REACT_APP_BACKEND_URL (CRA inlines it at `yarn build`;
 *      absent in CI / Docker builds, which keeps the image env-agnostic)
 *   4. same origin — the ingress routes /api/* to the backend
 *
 *  Import it ALIASED in components —
 *      import { getBackendUrl as resolveBackendUrl } from "@/lib/runtimeConfig";
 *  — so a downstream fork that also declares `getBackendUrl` never hits
 *  "Identifier 'getBackendUrl' has already been declared" after a merge.
 *  Call it at use time (not module load) so late runtime config still wins. */
const RUNTIME_GLOBALS = ["__ENV__", "_env_", "env", "__RUNTIME_CONFIG__", "runtimeConfig"];
const RUNTIME_KEYS = ["BACKEND_URL", "REACT_APP_BACKEND_URL", "API_URL"];

// Read at call time (CRA inlines `process.env` at build; tests can unset it).
function buildTimeUrl() {
    return process.env["REACT_APP_BACKEND_URL"] || "";
}

function stripSlash(v) {
    return String(v || "").trim().replace(/\/+$/, "");
}

function fromRuntimeConfig(w) {
    for (const g of RUNTIME_GLOBALS) {
        const cfg = w[g];
        if (!cfg || typeof cfg !== "object") continue;
        for (const k of RUNTIME_KEYS) {
            if (cfg[k]) return String(cfg[k]);
        }
    }
    return "";
}

/** Backend HTTP(S) origin without trailing slash, e.g. "https://ai.sitecentric.us". */
export function getBackendUrl() {
    const w = typeof window !== "undefined" ? window : {};
    const raw = fromRuntimeConfig(w) || w.REACT_APP_BACKEND_URL || buildTimeUrl()
        || (w.location && w.location.origin) || "";
    return stripSlash(raw);
}

/** Same origin as getBackendUrl() with the ws(s) scheme, e.g. "wss://ai.sitecentric.us". */
export function getBackendWsUrl() {
    return getBackendUrl().replace(/^http(s?):/i, "ws$1:");
}

/** Convenience: `${getBackendUrl()}/api`. */
export function getApiUrl() {
    return `${getBackendUrl()}/api`;
}

if (typeof window !== "undefined" && typeof window.getBackendUrl !== "function") {
    window.getBackendUrl = getBackendUrl;
}

export default getBackendUrl;
